/**
 * /staff-room — Private, staff-only messaging.
 *
 *  ▪ RBAC-gated (backend returns 403 for non-staff)
 *  ▪ Channels + DMs sidebar, main message panel, composer with @student mentions
 *  ▪ Polls every 5s (?since cursor) — no websockets, keeps infra light
 *  ▪ Student profile deep-links: click a @mention pill → opens student profile
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, Hash, Lock, Send, AlertTriangle, ShieldAlert, ArrowLeft, Plus,
  Users, AtSign, User, Check, X, Paperclip,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useAttachments, AttachmentPreview } from '../lib/attachments';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const POLL_MS = 5000;

// ─── Message body renderer — highlights @student pills ───────────────────────
const renderBody = (msg, onOpenStudent) => {
  const body = msg.body || '';
  if (!msg.mentions?.length) return <span>{body}</span>;
  // Replace tokens like "@Name" with pill buttons
  const pieces = [body];
  msg.mentions.forEach((m, i) => {
    const token = `@${m.student_name}`;
    for (let j = pieces.length - 1; j >= 0; j--) {
      const seg = pieces[j];
      if (typeof seg !== 'string' || !seg.includes(token)) continue;
      const idx = seg.indexOf(token);
      const before = seg.slice(0, idx);
      const after = seg.slice(idx + token.length);
      pieces.splice(j, 1,
        before,
        <button
          key={`m-${i}-${j}`}
          onClick={() => onOpenStudent(m)}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 bg-amber-500/15 border border-amber-500/40 rounded text-amber-300 text-[11px] font-black hover:bg-amber-500/25"
          data-testid={`mention-${m.student_id}`}
        >
          <AtSign className="w-2.5 h-2.5" />{m.student_name}
        </button>,
        after,
      );
    }
  });
  return <>{pieces.map((p, i) => (typeof p === 'string' ? <span key={i}>{p}</span> : p))}</>;
};

// ─── Mention autocomplete ────────────────────────────────────────────────────
const useMentionSearch = () => {
  const [results, setResults] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/academy/staff/students/search?q=${encodeURIComponent(q)}`, { headers: authHeaders() });
        setResults(r.data.students || []);
      } catch {}
    }, 180);
    return () => clearTimeout(t);
  }, [q]);
  return { q, setQ, results };
};

// ─── New Channel modal ───────────────────────────────────────────────────────
const NewChannelModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priv, setPriv] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/academy/staff/channels', { name, description, private: priv }, { headers: authHeaders() });
      toast.success(`#${r.data.channel.name} created`);
      onCreated(r.data.channel);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="new-channel-modal">
      <div className="w-full max-w-md bg-zinc-950 border-2 border-zinc-800 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black uppercase tracking-[0.2em] text-white">New Channel</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '-'))} placeholder="e.g. maddox-sterling-tier3" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none" data-testid="new-channel-name" />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Purpose (optional)" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none" data-testid="new-channel-description" />
        <label className="flex items-center gap-2 text-xs text-zinc-400 mb-4">
          <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} data-testid="new-channel-private" />
          Private (invitees only)
        </label>
        <button onClick={save} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="new-channel-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create Channel
        </button>
      </div>
    </div>
  );
};

// ─── DM launcher modal ───────────────────────────────────────────────────────
const NewDMModal = ({ onClose, onOpened }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  useEffect(() => {
    api.get('/academy/staff/directory', { headers: authHeaders() })
      .then((r) => setStaff(r.data.staff || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const open = async (email) => {
    try {
      const r = await api.post('/academy/staff/dm', { email }, { headers: authHeaders() });
      onOpened(r.data.channel);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not open DM'); }
  };
  const filtered = staff.filter((s) => {
    const n = (s.name || '').toLowerCase();
    const e = (s.email || '').toLowerCase();
    const term = q.toLowerCase();
    return !term || n.includes(term) || e.includes(term);
  });
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="new-dm-modal">
      <div className="w-full max-w-md bg-zinc-950 border-2 border-zinc-800 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black uppercase tracking-[0.2em] text-white">Direct Message</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none" data-testid="dm-search" autoFocus />
        <div className="max-h-[50vh] overflow-y-auto space-y-1">
          {loading ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div> : (
            filtered.length === 0 ? <p className="text-xs text-zinc-500 py-4 text-center">No staff match.</p> :
              filtered.map((s) => (
                <button key={s.id} onClick={() => open(s.email)} className="w-full flex items-center justify-between p-3 border border-zinc-900 hover:border-orange-500 hover:bg-white/[0.02] transition-colors text-left" data-testid={`dm-target-${s.id}`}>
                  <div>
                    <div className="text-sm font-black text-white">{s.name || s.email}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{s.email}</div>
                  </div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">{s.is_admin ? 'Admin' : s.role}</div>
                </button>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

const StaffRoom = () => {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [me, setMe] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [denied, setDenied] = useState(false);
  const [draft, setDraft] = useState('');
  const [pendingMentions, setPendingMentions] = useState([]); // {student_id, student_name}
  const [showMention, setShowMention] = useState(false);
  const { q: mentionQ, setQ: setMentionQ, results: mentionResults } = useMentionSearch();
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const sinceRef = useRef(null);
  const listEndRef = useRef(null);
  const pollRef = useRef(null);
  const msgAtt = useAttachments();

  // Load channels once
  const loadChannels = useCallback(async () => {
    try {
      const r = await api.get('/academy/staff/channels', { headers: authHeaders() });
      setChannels(r.data.channels || []);
      setMe(r.data.me);
      if (!activeId && r.data.channels?.length) {
        setActiveId(r.data.channels[0].id);
      }
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error('Could not load Staff Room');
    } finally { setLoadingChannels(false); }
  }, [activeId]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // Load messages for active channel
  useEffect(() => {
    if (!activeId) return;
    setLoadingMessages(true);
    setMessages([]);
    sinceRef.current = null;
    api.get(`/academy/staff/channels/${activeId}/messages`, { headers: authHeaders() })
      .then((r) => {
        setMessages(r.data.messages || []);
        sinceRef.current = r.data.now || (r.data.messages || []).slice(-1)[0]?.created_at;
        api.post(`/academy/staff/channels/${activeId}/read`, {}, { headers: authHeaders() }).catch(() => {});
      })
      .catch(() => toast.error('Could not load messages'))
      .finally(() => setLoadingMessages(false));
  }, [activeId]);

  // 5s polling for new messages
  useEffect(() => {
    if (!activeId) return;
    const poll = async () => {
      if (!sinceRef.current) return;
      try {
        const r = await api.get(`/academy/staff/channels/${activeId}/messages?since=${encodeURIComponent(sinceRef.current)}`, { headers: authHeaders() });
        const newMsgs = r.data.messages || [];
        if (newMsgs.length) {
          setMessages((prev) => [...prev, ...newMsgs]);
          sinceRef.current = r.data.now || newMsgs[newMsgs.length - 1].created_at;
          api.post(`/academy/staff/channels/${activeId}/read`, {}, { headers: authHeaders() }).catch(() => {});
        } else {
          sinceRef.current = r.data.now || sinceRef.current;
        }
      } catch {}
    };
    pollRef.current = setInterval(poll, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [activeId]);

  // Auto-scroll on new messages
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const activeChannel = useMemo(() => channels.find((c) => c.id === activeId), [channels, activeId]);

  const send = async () => {
    if (!draft.trim() && !pendingMentions.length && msgAtt.attachments.length === 0) return;
    try {
      const r = await api.post(`/academy/staff/channels/${activeId}/messages`, {
        body: draft,
        mentions: pendingMentions,
        attachments: msgAtt.attachments,
      }, { headers: authHeaders() });
      setMessages((prev) => [...prev, r.data.message]);
      sinceRef.current = r.data.message.created_at;
      setDraft('');
      setPendingMentions([]);
      msgAtt.reset();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
  };

  const handleDraftChange = (v) => {
    setDraft(v);
    // Detect trailing "@..." token for mention autocomplete
    const m = v.match(/@([\w-]{0,32})$/);
    if (m) { setShowMention(true); setMentionQ(m[1]); }
    else { setShowMention(false); setMentionQ(''); }
  };

  const pickMention = (student) => {
    // Replace trailing @... with @Name
    const newDraft = draft.replace(/@([\w-]*)$/, `@${student.name || student.email} `);
    setDraft(newDraft);
    setPendingMentions((prev) => {
      if (prev.some((p) => p.student_id === student.id)) return prev;
      return [...prev, { student_id: student.id, student_name: student.name || student.email, student_email: student.email }];
    });
    setShowMention(false);
    setMentionQ('');
  };

  const openStudent = (m) => {
    // Deep-link into admin user profile (staff-only route). Fallback: `/admin/users?q=`
    navigate(`/admin/users?q=${encodeURIComponent(m.student_email || m.student_name)}`);
  };

  if (denied) return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center p-6">
      <div className="max-w-md text-center border-2 border-red-500/40 bg-red-500/5 p-8">
        <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h2 className="text-2xl font-black uppercase text-white">Staff Only</h2>
        <p className="text-zinc-400 text-sm mt-2">The Staff Room is restricted to faculty, coaches, and administrators.</p>
        <Link to="/academy" className="mt-6 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-orange-500 hover:text-orange-400">
          <ArrowLeft className="w-3 h-3" /> Back to Academy
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex flex-col" data-testid="staff-room">
      {/* Header */}
      <div className="border-b-2 border-zinc-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/academy" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Academy
          </Link>
          <span className="text-zinc-700">·</span>
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-amber-400">The Staff Room</div>
        </div>
        <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
          {me?.name || me?.email} · <span className="text-zinc-400">Staff</span>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 border-r-2 border-zinc-900 bg-[#080A10] overflow-y-auto flex-shrink-0">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] tracking-[0.4em] uppercase font-black text-zinc-500">Channels</div>
              <button onClick={() => setShowNewChannel(true)} className="text-zinc-500 hover:text-orange-500" title="New channel" data-testid="new-channel-btn">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            {loadingChannels ? <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-600" /></div> : (
              <div className="space-y-0.5">
                {channels.filter((c) => c.kind !== 'dm').map((c) => (
                  <button key={c.id} onClick={() => setActiveId(c.id)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-left text-sm rounded ${activeId === c.id ? 'bg-orange-500/15 text-white' : 'text-zinc-400 hover:bg-white/[0.02] hover:text-white'}`}
                    data-testid={`channel-btn-${c.slug || c.id}`}>
                    <span className="flex items-center gap-1.5 truncate">
                      {c.is_emergency ? <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" /> :
                        (c.is_default_all_staff ? <Hash className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" /> : <Lock className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />)}
                      <span className="truncate">{c.name}</span>
                    </span>
                    {c.unread > 0 && <span className="ml-2 text-[9px] font-black bg-orange-500 text-black px-1.5 rounded">{c.unread}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-4 border-t border-zinc-900">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] tracking-[0.4em] uppercase font-black text-zinc-500">Direct</div>
              <button onClick={() => setShowNewDM(true)} className="text-zinc-500 hover:text-orange-500" title="New DM" data-testid="new-dm-btn">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-0.5">
              {channels.filter((c) => c.kind === 'dm').map((c) => (
                <button key={c.id} onClick={() => setActiveId(c.id)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-left text-sm rounded ${activeId === c.id ? 'bg-orange-500/15 text-white' : 'text-zinc-400 hover:bg-white/[0.02] hover:text-white'}`}
                  data-testid={`dm-btn-${c.id}`}>
                  <span className="flex items-center gap-1.5 truncate">
                    <User className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                    <span className="truncate">{c.name}</span>
                  </span>
                  {c.unread > 0 && <span className="ml-2 text-[9px] font-black bg-orange-500 text-black px-1.5 rounded">{c.unread}</span>}
                </button>
              ))}
              {channels.filter((c) => c.kind === 'dm').length === 0 && <p className="text-[10px] text-zinc-600 px-2">Click + to start a DM.</p>}
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Channel header */}
          {activeChannel && (
            <div className="border-b border-zinc-900 px-6 py-3 flex items-center justify-between" data-testid="active-channel-header">
              <div>
                <div className="flex items-center gap-2 text-white font-black">
                  {activeChannel.is_emergency ? <AlertTriangle className="w-4 h-4 text-red-400" /> :
                    (activeChannel.kind === 'dm' ? <User className="w-4 h-4 text-zinc-400" /> : <Hash className="w-4 h-4 text-zinc-400" />)}
                  {activeChannel.name}
                </div>
                {activeChannel.description && <p className="text-[11px] text-zinc-500 mt-0.5">{activeChannel.description}</p>}
              </div>
              {activeChannel.is_emergency && (
                <span className="text-[9px] font-black uppercase tracking-widest text-red-400 border border-red-500/40 bg-red-500/10 px-2 py-1">
                  Admin-Only Broadcast
                </span>
              )}
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="messages-list">
            {loadingMessages ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div> :
              messages.length === 0 ? <p className="text-center text-zinc-600 text-sm py-10">No messages yet. Break the ice.</p> :
                messages.map((m) => (
                  <div key={m.id} className="flex gap-3" data-testid={`msg-${m.id}`}>
                    <div className="w-8 h-8 flex-shrink-0 border-2 border-zinc-800 bg-zinc-950 flex items-center justify-center text-[10px] font-black text-zinc-400">
                      {(m.sender_name || m.sender_email || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-black text-white text-sm">{m.sender_name || m.sender_email}</span>
                        <span className="text-[10px] text-zinc-600">{(m.created_at || '').replace('T', ' ').slice(0, 16)} UTC</span>
                      </div>
                      <div className="text-zinc-200 text-sm leading-relaxed mt-0.5 whitespace-pre-wrap break-words">
                        {renderBody(m, openStudent)}
                      </div>
                      {(m.attachments || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {m.attachments.map((a, i) => <AttachmentPreview key={i} att={a} />)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
            }
            <div ref={listEndRef} />
          </div>

          {/* Composer */}
          {activeChannel && (
            <div className="border-t-2 border-zinc-900 p-4 relative">
              {showMention && mentionResults.length > 0 && (
                <div className="absolute bottom-full left-4 right-4 mb-2 bg-zinc-950 border-2 border-zinc-800 max-h-56 overflow-y-auto z-10" data-testid="mention-dropdown">
                  <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500 px-3 py-2 border-b border-zinc-900">Tag Student</div>
                  {mentionResults.map((s) => (
                    <button key={s.id} onClick={() => pickMention(s)} className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-orange-500/10" data-testid={`mention-pick-${s.id}`}>
                      <div>
                        <div className="text-sm font-black text-white">{s.name || s.email}</div>
                        <div className="text-[10px] text-zinc-500">{s.email}{s.grade_level ? ` · G${s.grade_level}` : ''}{s.sport ? ` · ${s.sport}` : ''}</div>
                      </div>
                      <AtSign className="w-3.5 h-3.5 text-amber-400" />
                    </button>
                  ))}
                </div>
              )}
              {pendingMentions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {pendingMentions.map((p) => (
                    <span key={p.student_id} className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-amber-500/15 border border-amber-500/40 text-amber-300 px-2 py-1" data-testid={`pending-mention-${p.student_id}`}>
                      <AtSign className="w-2.5 h-2.5" />{p.student_name}
                      <button onClick={() => setPendingMentions((prev) => prev.filter((x) => x.student_id !== p.student_id))} className="ml-1 hover:text-white"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
              )}
              {msgAtt.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid="staff-msg-attachments">
                  {msgAtt.attachments.map((a, i) => (
                    <AttachmentPreview key={i} att={a} onRemove={() => msgAtt.removeAt(i)} />
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-end">
                <textarea
                  value={draft}
                  onChange={(e) => handleDraftChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !showMention) { e.preventDefault(); send(); } }}
                  placeholder={activeChannel.is_emergency && !me?.is_admin ? 'Only admins can broadcast here…' : 'Message… (type @ to tag a student profile)'}
                  disabled={activeChannel.is_emergency && !me?.is_admin}
                  className="flex-1 bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none resize-none disabled:opacity-40"
                  rows={2}
                  data-testid="msg-composer"
                />
                <label className="border-2 border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500 px-3 py-2.5 cursor-pointer" data-testid="msg-attach" title="Attach">
                  {msgAtt.uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                  <input type="file" accept="image/*,video/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) msgAtt.addFile(f); e.target.value = ''; }} />
                </label>
                <button
                  onClick={send}
                  disabled={msgAtt.uploading || (!draft.trim() && !pendingMentions.length && msgAtt.attachments.length === 0)}
                  className="bg-orange-500 hover:bg-orange-400 text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="msg-send-btn"
                >
                  <Send className="w-3.5 h-3.5" /> Send
                </button>
              </div>
              <p className="text-[10px] text-zinc-600 mt-1.5">Type <b className="text-amber-400">@</b> to tag a student profile. Tags are staff-only — students never see this room.</p>
            </div>
          )}
        </div>
      </div>

      {showNewChannel && <NewChannelModal onClose={() => setShowNewChannel(false)} onCreated={(c) => { setShowNewChannel(false); loadChannels(); setActiveId(c.id); }} />}
      {showNewDM && <NewDMModal onClose={() => setShowNewDM(false)} onOpened={(c) => { setShowNewDM(false); loadChannels(); setActiveId(c.id); }} />}
    </div>
  );
};

export default StaffRoom;
